import React, { useMemo } from 'react';
import { SxStyle } from '../../types';
import { useStringsContext } from '../../contexts/strings';
import Box from '@mui/material/Box';
import { DropzoneOptions, FileError, FileRejection, useDropzone } from 'react-dropzone';

export type FileWithDimensions = File & { width: number; height: number };
export type FileRejectionWithDimensions = {
  file: FileWithDimensions;
} & FileRejection;

const styles: SxStyle = {
  baseStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
    height: '100%',
  },
  focusedStyle: {
    borderColor: '#2196f3',
  },
  acceptStyle: {
    borderColor: '#00e676',
  },
  rejectStyle: {
    borderColor: '#ff1744',
  },
};

export interface ImageDropzoneProps extends Pick<DropzoneOptions, 'maxFiles' | 'maxSize'> {
  onAcceptedFiles: (files: FileWithDimensions[]) => void;
  onRejectedFiles?: (files: FileRejectionWithDimensions[]) => void;
  onValidator?: (file: FileWithDimensions) => FileError | null;
}

const ImageDropzone = ({
  onAcceptedFiles,
  onRejectedFiles,
  onValidator,
  maxSize,
  maxFiles = 1,
}: ImageDropzoneProps) => {
  const { strings } = useStringsContext();

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    maxSize,
    maxFiles,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/gif': [],
      'image/webp': [],
    },
    onDrop: (acceptedFiles, fileRejections) => {
      onAcceptedFiles(acceptedFiles as unknown as FileWithDimensions[]);
      onRejectedFiles &&
        onRejectedFiles(fileRejections as unknown as FileRejectionWithDimensions[]);
    },
    getFilesFromEvent: async (event: any) => {
      const files = event.target.files || event.dataTransfer.files;
      const promises = [];
      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        const promise = new Promise<FileWithDimensions>((resolve) => {
          const image = new Image();
          image.onload = function () {
            file.width = image.width;
            file.height = image.height;
            resolve(file);
          };
          image.src = URL.createObjectURL(file);
        });
        promises.push(promise);
      }
      return await Promise.all(promises);
    },
    validator: (file) => {
      return onValidator ? onValidator(file as unknown as FileWithDimensions) : null;
    },
  });

  const style = useMemo(
    () => ({
      ...styles.baseStyle,
      ...(isFocused ? styles.focusedStyle : {}),
      ...(isDragAccept ? styles.acceptStyle : {}),
      ...(isDragReject ? styles.rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  return (
    <Box {...getRootProps({ style } as any)}>
      <input {...getInputProps()} />
      {
        <p>
          {maxFiles === 1 ? strings.messages.dragAndDropImage : strings.messages.dragAndDropImages}
        </p>
      }
    </Box>
  );
};

export default ImageDropzone;
